import {
  clone,
  complement,
  compose,
  filter,
  flatten,
  isEmpty,
  isNil,
  length,
  map,
  pathOr,
  pluck,
  prop,
  sum,
  uniq,
} from 'ramda'
import {
  ANALOG_INPUT,
  DIGITAL_INPUT,
  DIGITAL_OUTPUT,
  PWM,
  AMPS20,
  MAX_CAN_INPUTS,
  MAX_SWITCH_INDICATORS,
  CAN_OUTPUT,
  TIMERS,
  LOGIC_SETS,
  CAN_INPUTS,
  SWITCH_INDICATORS,
} from 'utils/constants'
import { callOr, isEmptyOrNil } from 'utils/functional'
import { findMostRecentRevision } from 'utils/revisionHelpers'
import { updateVehicleFeatures, validateVehicleFeatures } from 'actions/truck'
import strings from 'utils/strings'
import { insertIf } from 'utils/types'

import { PWM_MODES_MAPPING } from 'constants/featureOutputs'

import {
  virtualFuseMask,
  virtualFuseReset,
  polarityLabels,
  keepAwakeLabel,
  powerNetLevels,
  pwmModeTitle,
} from 'i18n/displayConstants/featureOutputs'
import { MAX_NUMBER_LOGICS, MAX_NUMBER_TIMERS } from './constants'

const ALLOWED_UPDATE_STATUSES = ['active', 'test']

/**
 * Determines if updating a feature would exceed CAN input or switch indicator limits.
 * Handles both snapshot updates (latestOnVehicle === false) and revision updates
 * (isFeatureLatestRevision === false with eligible newer revisions).
 */
export const exceedsSetBasedLimits = (record, features, vehicleECUs) => {
  if (!features || !record) return false
  // When a feature is marked for deletion, add-back restores the current
  // version — not the newer update — so set-based update limits don't apply.
  if (record.isMarkedForDeletion) return false
  const { moduleName, latestOnVehicle, isFeatureLatestRevision, revisions } =
    record

  // Determine the updated feature data source
  let updatedFeatureData = null

  if (latestOnVehicle === false) {
    // Snapshot update — only consider if snapshot has eligible status
    const snapshotFeature = record.latestSnapshot?.object?.feature
    if (
      snapshotFeature?.status &&
      ALLOWED_UPDATE_STATUSES.includes(snapshotFeature.status.toLowerCase())
    ) {
      updatedFeatureData = snapshotFeature
    }
  } else if (
    isFeatureLatestRevision === false &&
    revisions &&
    revisions.length > 0
  ) {
    // Revision update - find the newest eligible revision
    const eligible = revisions.filter(
      (r) =>
        r.status && ALLOWED_UPDATE_STATUSES.includes(r.status.toLowerCase())
    )
    if (eligible.length > 0) {
      const newest = findMostRecentRevision(eligible)
      if (newest) {
        const newestRev = newest.revision || newest.release || ''
        const currentRev = record.revision || ''
        if (newestRev !== currentRev) {
          updatedFeatureData = newest
        }
      }
    }
  }

  if (!updatedFeatureData) return false

  const matchingEcu =
    vehicleECUs && vehicleECUs.find((ecu) => ecu.id === moduleName)
  const maxSwitchIndicator =
    (matchingEcu && matchingEcu.switchIndicatorMax) || MAX_SWITCH_INDICATORS
  const maxCanInput =
    (matchingEcu && matchingEcu.maxCanInputs) || MAX_CAN_INPUTS

  const otherModuleFeatures = features.filter(
    (f) =>
      f.moduleName === moduleName &&
      f.id !== record.id &&
      !f.isMarkedForDeletion
  )

  const updatedSwitchIndicators = updatedFeatureData.switchIndicators
  if (updatedSwitchIndicators && updatedSwitchIndicators.length > 0) {
    const switchSet = new Set(
      otherModuleFeatures.flatMap((f) => f.switchIndicators || [])
    )
    updatedSwitchIndicators.forEach((si) => switchSet.add(si))
    if (switchSet.size > maxSwitchIndicator) return true
  }

  const updatedCanInputs = updatedFeatureData.canInputs
  if (updatedCanInputs && updatedCanInputs.length > 0) {
    const canSet = new Set(
      otherModuleFeatures.flatMap((f) => f.canInputs || [])
    )
    updatedCanInputs.forEach((ci) => canSet.add(ci))
    if (canSet.size > maxCanInput) return true
  }

  return false
}

const getGroupedFeatures = (features, isWithoutChanges = false) => {
  const filteredFeatures = isWithoutChanges
    ? features.filter((ft) => !ft.isMarkedForAddition)
    : features
  return filteredFeatures.reduce(
    (acc, curr) => ({
      ...acc,
      [curr.moduleName]: [
        ...(acc[curr.moduleName] ? acc[curr.moduleName] : []),
        curr,
      ],
    }),
    {}
  )
}

const getTotals = (grouped, type) => {
  const keyMap = {
    [LOGIC_SETS]: 'complexQueryCount',
    [TIMERS]: 'timerQueryCount',
    [CAN_INPUTS]: 'canInputs',
    [SWITCH_INDICATORS]: 'switchIndicators',
  }

  const useSetCount = type === CAN_INPUTS || type === SWITCH_INDICATORS

  return Object.entries(grouped).reduce(
    (acc, [key, value]) => ({
      ...acc,
      [key]: useSetCount
        ? [
            ...new Set(
              value.flatMap((v) => v[keyMap[type]] || []).filter((item) => item)
            ),
          ].length
        : sum(value.map((v) => v[keyMap[type]])),
    }),
    {}
  )
}

const getValidCapacity = (value, fallback) =>
  typeof value === 'number' && Number.isFinite(value) && value > 0
    ? value
    : fallback

const getModuleCapacity = (modules, moduleId, capacityName, fallback) => {
  const module = (modules || []).find(({ id }) => id === moduleId)
  return getValidCapacity(module && module[capacityName], fallback)
}

export const getEcusOverMax = (type, features, modules) => {
  if (!type || !features) return false

  const groupedWithoutChanges = getGroupedFeatures(features, true)

  const grouped = getGroupedFeatures(features)

  const totalsWithoutChanges = getTotals(groupedWithoutChanges, type)

  const totals = getTotals(grouped, type)

  const isOverMax = (value, valueWithoutChanges, moduleId) => {
    if (
      type === LOGIC_SETS &&
      value !== valueWithoutChanges &&
      value >
        getModuleCapacity(modules, moduleId, 'maxLogic', MAX_NUMBER_LOGICS)
    )
      return true
    if (
      type === TIMERS &&
      value !== valueWithoutChanges &&
      value >
        getModuleCapacity(modules, moduleId, 'maxTimer', MAX_NUMBER_TIMERS)
    )
      return true
    if (
      type === CAN_INPUTS &&
      value !== valueWithoutChanges &&
      value > MAX_CAN_INPUTS
    )
      return true
    if (
      type === SWITCH_INDICATORS &&
      value !== valueWithoutChanges &&
      value > MAX_SWITCH_INDICATORS
    )
      return true

    return false
  }

  const overMax = Object.entries(totals).reduce(
    (acc, [key, value]) => ({
      ...acc,
      [key]: isOverMax(value, totalsWithoutChanges[key], key),
    }),
    {}
  )

  return overMax
}

export const getMaxNotificationString = (type, features, modules) => {
  const maxChecks = getEcusOverMax(type, features, modules)
  const ecusList = Object.entries(maxChecks)
    .reduce((acc, [key, value]) => {
      if (!value) return acc

      return [...acc, key]
    }, [])
    .sort()
    .join(', ')

  if (!ecusList) return ''

  const logicTypeStrings = {
    LOGIC_SETS: strings.logic_sets,
    TIMERS: strings.timers,
    CAN_INPUTS: strings.can_inputs,
    SWITCH_INDICATORS: strings.switch_indicators,
  }

  return strings.you_have_exceeded_the_max(logicTypeStrings[type], ecusList)
}

export const getUnresolvedConflictsCounts = (conflicts) => {
  if (!conflicts) return [0, 0, 0, 0]

  const countUnresolvedConflictPins = (modulesConflicts) => {
    const modules = Object.values(modulesConflicts)
    let count = 0

    modules.forEach((module) => {
      const pins = Object.values(module)

      pins.forEach((pin) => {
        if (pin.selection === null) count += 1
      })
    })

    return count
  }

  const counts = [DIGITAL_INPUT, ANALOG_INPUT, DIGITAL_OUTPUT, CAN_OUTPUT].map(
    (key) => (conflicts[key] ? countUnresolvedConflictPins(conflicts[key]) : 0)
  )

  return counts
}

export const notProp = complement(prop)
export const filterProp = (property) => filter(prop(property))
export const mapUri = map(prop('uri'))
export const urisFilteredBy = (property) =>
  compose(mapUri, filterProp(property))

// Canonical URI resolution — use everywhere a feature's identifier is needed.
// Covers both top-level uri and objectUri, and their previousSnapshot variants.
export const resolveFeatureUri = (f) =>
  f.uri ||
  f.previousSnapshot?.uri ||
  f.objectUri ||
  f.previousSnapshot?.objectUri

export const resolvePreviousUri = (f) =>
  f.previousSnapshot?.uri ||
  f.previousSnapshot?.objectUri ||
  f.uri ||
  f.objectUri

// Extract URI and revision from feature objects for API calls
// This ensures revision data is sent to the backend for history tracking
export const mapUriAndRevision = map(({ uri, revision }) => ({ uri, revision }))
export const featuresWithRevisionFilteredBy = (property) =>
  compose(mapUriAndRevision, filterProp(property))

// For updated features, the removed entry should reference the original version
// (stored in previousSnapshot), not the updated version at the top level
export const previousVersionsFilteredByUpdate = (set) =>
  set
    .filter((f) => f.isMarkedForUpdate && f.previousSnapshot)
    .map((f) => ({
      uri: f.previousSnapshot.uri,
      revision: f.previousSnapshot.revision,
    }))

export const previousUrisFilteredByUpdate = (set) =>
  set
    .filter((f) => f.isMarkedForUpdate && f.previousSnapshot)
    .map((f) => f.previousSnapshot.uri)

/**
 * Save added/removed/updated features and switches for a vehicle.
 * Dispatches via the centralized apiAction error handler chain.
 *
 * @param {Function} dispatch - Redux dispatch (passed from the calling component)
 * @param {string}   serialNumber
 * @param {object[]} onGoingSet      - Working set of features with isMarkedFor* flags
 * @param {string}   commitMessage   - Change notes
 * @param {object}   addedSwitches   - Map of switch panels to add
 * @param {object}   removedSwitches - Map of switch panels to remove
 * @param {object[]} addedSolenoids  - Solenoids to add
 */
export const postUpdates = (
  dispatch,
  serialNumber,
  onGoingSet,
  commitMessage,
  addedSwitches,
  removedSwitches,
  addedSolenoids
) => {
  const addedFeatures = onGoingSet
    .filter((f) => f.isMarkedForAddition)
    .map((f) => ({
      uri: resolveFeatureUri(f),
      revision: f.revision,
    }))
    .filter((entry) => entry.uri)
  const removedFeatures = uniq([
    ...featuresWithRevisionFilteredBy('isMarkedForDeletion')(onGoingSet),
    ...onGoingSet
      .filter((f) => f.isMarkedForUpdate)
      .map((f) => ({
        uri: resolvePreviousUri(f),
        revision: f.previousSnapshot?.revision || f.revision,
      }))
      .filter((entry) => entry.uri),
  ])

  const body = {
    addedFeatures,
    removedFeatures,
    notes: commitMessage,
    addedSolenoids,
  }

  if (!isNil(addedSwitches) && !isEmpty(Object.values(addedSwitches))) {
    body.addedSwitches = uniq(Object.values(addedSwitches))
  }

  if (!isNil(removedSwitches) && !isEmpty(Object.values(removedSwitches))) {
    body.removedSwitches = uniq(Object.values(removedSwitches))
  }

  return dispatch(updateVehicleFeatures(serialNumber, body))
}

// an updated feature will be in both added/removed list
const requestFeaturesValidation = (dispatch, serialNumber, updatedSet) =>
  dispatch(
    validateVehicleFeatures(serialNumber, {
      addedFeatures: updatedSet
        .filter((f) => f.isMarkedForAddition)
        .map(resolveFeatureUri)
        .filter(Boolean),
      removedFeatures: uniq([
        ...updatedSet
          .filter((f) => f.isMarkedForUpdate)
          .map(resolvePreviousUri)
          .filter(Boolean),
        ...urisFilteredBy('isMarkedForDeletion')(updatedSet),
      ]),
    })
  )

/**
 * Check if any of the given switch panels are already assigned to the vehicle.
 * Dispatches via the centralized apiAction error handler chain.
 *
 * @param {Function} dispatch         - Redux dispatch (passed from the calling component)
 * @param {string}   serialNumber
 * @param {object[]} addedSwitchPanels
 * @returns {{ panels: string[], ok: boolean }}
 */
export const getSwitchesAlreadyOnVehicle = async (
  dispatch,
  serialNumber,
  addedSwitchPanels
) => {
  if (!addedSwitchPanels || addedSwitchPanels.length === 0)
    return { panels: [], ok: true }
  // Network/server errors are NOT caught — they propagate to the caller so the
  // calling component (Commit.jsx, AddRemoveVehicleFeatures.jsx) can dispatch an
  // error notification. Without propagating, the user would see nothing when a
  // network error blocks the feature save — validateVehicleFeatures has
  // readOnly: true so defaultErrorHandler is silent for these failures.
  const response = await dispatch(
    validateVehicleFeatures(serialNumber, {
      addedSwitches: addedSwitchPanels,
    })
  )
  // apiCall.js resolves with undefined when a call is de-duplicated (another
  // identical call is already in-flight). Under de-dup the validation result
  // is genuinely unknown — aborting (ok: false) is the safe default for a
  // pre-save check. Proceeding with ok: true would allow the save to continue
  // without confirmation, which is unsafe if two save paths are racing.
  if (response === undefined) return { panels: [], ok: false }
  const data = response?.data ?? response
  return { panels: data?.switchesAlreadyOnVehicle || [], ok: true }
}

/**
 * Validate a feature working-set for module I/O conflicts.
 * Dispatches via the centralized apiAction error handler chain so that
 * network errors show the default error modal.
 *
 * @param {Function} dispatch  - Redux dispatch (passed from the calling component)
 * @param {string}   serialNumber
 * @param {object[]} set       - Feature working set with isMarkedFor* flags
 * @returns {{ currentConflictingFeatures?: object, err?: Error } | false}
 */
export const validateAndGetConflicts = async (dispatch, serialNumber, set) => {
  try {
    const response = await requestFeaturesValidation(
      dispatch,
      serialNumber,
      set
    )
    // apiCall.js resolves with undefined when the call is de-duplicated (another
    // identical validateVehicleFeatures call is already in-flight for this vehicle).
    // Return an object (not bare false) so callers can safely destructure:
    //   const { currentConflictingFeatures = false } = await validateAndGetConflicts(...)
    // Returning bare false would throw a TypeError on destructuring.
    if (response === undefined) return { currentConflictingFeatures: false }
    const data = response?.data ?? response

    const {
      moduleInputConflicts = [],
      moduleOutputConflicts = [],
      moduleCanOutputConflicts = [],
    } = data

    const hasConflicts =
      !isEmpty(moduleInputConflicts) ||
      !isEmpty(moduleOutputConflicts) ||
      !isEmpty(moduleCanOutputConflicts)

    if (hasConflicts) {
      const formatConflicts = (conflicts, type) => {
        const results = conflicts.reduce(
          (acc, curr) => ({
            ...acc,
            [curr.pinType || type]: {
              ...acc[curr.pinType || type],
              [curr.module]: {
                ...pathOr({}, [curr.pinType || type, curr.module], acc),
                [curr.pin || curr.canId]: {
                  ...curr,
                  selection: null, // the choosen conflict resolution option from curr.inputFeatures || curr.outputFeatures
                },
              },
            },
          }),
          {}
        )

        /**
         * Resulting shape
         *
         * {
         *  pintType: {
         *    module: {
         *      pin: { ...conflictObj, selection }
         *      pin: { ...conflictObj, selection }
         *    },
         *    module: {
         *      pin: { ...conflictObj, selection }
         *      pin: { ...conflictObj, selection }
         *    }
         *  },
         * }
         */

        return results
      }

      const formatConflictsWithDefault = callOr([], formatConflicts)

      const formattedInputConflicts =
        formatConflictsWithDefault(moduleInputConflicts)
      const formattedOutputConflicts = formatConflictsWithDefault(
        moduleOutputConflicts
      )
      const formattedCanOutputConflicts = formatConflicts(
        moduleCanOutputConflicts || [],
        CAN_OUTPUT
      )

      return {
        currentConflictingFeatures: autoResolveSingleOptionConflicts({
          [DIGITAL_INPUT]: formattedInputConflicts[DIGITAL_INPUT],
          [ANALOG_INPUT]: formattedInputConflicts[ANALOG_INPUT],
          [DIGITAL_OUTPUT]: formattedOutputConflicts[DIGITAL_OUTPUT],
          [CAN_OUTPUT]: formattedCanOutputConflicts[CAN_OUTPUT],
        }),
      }
    }
  } catch (err) {
    // Return null (not false) for currentConflictingFeatures on error so callers
    // can distinguish a network/validation failure from a clean "no conflicts"
    // result. A caller reading { currentConflictingFeatures = false } would treat
    // both error and no-conflicts identically — returning null makes the error
    // case detectable with a simple null check.
    return {
      err,
      currentConflictingFeatures: null,
    }
  }

  return { currentConflictingFeatures: false }
}

export const getIOSwitchesList = compose(flatten, pluck('ioSwitches'))

export const getConflictingFeaturesThatWillBeRemoved = (
  currentConflictingFeatures,
  originalConflicts = null
) =>
  Object.entries(currentConflictingFeatures)
    .map(([type, group]) => {
      if (group) {
        return Object.entries(group).map(([moduleName, module]) => {
          const selectedPins = Object.entries(module).filter(
            ([, pin]) => pin.selection
          )

          return selectedPins
            .map(([pinKey, pin]) => {
              const { selection } = pin

              // For auto-resolved pins the cascade has already deleted losing
              // options from pin.outputConflicts / pin.features / pin.canFeatures.
              // Use the corresponding original pin (which still has all options
              // intact) as the source so bystander features from pruned losing
              // options are included in the removal payload.
              const originalPin =
                originalConflicts?.[type]?.[moduleName]?.[pinKey]
              const sourcePinForRemovals =
                originalPin && originalPin.selection === null
                  ? originalPin
                  : pin

              const pinClone = clone(sourcePinForRemovals)
              // if it is digital output then it will have outputConflicts
              if (pinClone && !isEmptyOrNil(pinClone.outputConflicts)) {
                delete pinClone.outputConflicts[selection]
                const remainingSelectionOptions = Object.keys(
                  pinClone.outputConflicts
                )
                const features = remainingSelectionOptions
                  .map((key) =>
                    Object.values(pinClone.outputConflicts[key].featureSet)
                  )
                  .flat()
                return features
              }

              if (pinClone.canFeatures) {
                return pinClone.canFeatures.filter(
                  (item) => item.featureId !== selection
                )
              }
              // delete selected item off clone, all other items are rejected items
              delete pinClone.features[selection]
              const features = Object.values(pinClone.features).flat()
              return features
            })
            .flat()
        })
      }
      return undefined
    })
    .filter((item) => Boolean(item)) // remove undefineds
    .reduce((acc, curr) => [...acc, ...curr.flat()], []) // flatten

// Extract all available identifiers from a conflict feature object.
// Multiple fields guard against inconsistent shapes across conflict types.
// featureUri is the most canonical cross-type identifier (URI path to the feature).
const getFeatureKeys = (feature) =>
  [
    feature.featureId,
    feature.partNumber,
    feature.id,
    feature.featureUri,
  ].filter(Boolean)

/**
 * Given a resolved pin (selection !== null), return all identifier values
 * for every option that was NOT chosen — i.e. the features the user rejected.
 *
 * Uses pin.pinType (not field-emptiness) to select the correct branch.
 * API conflict objects carry BOTH features and outputConflicts on the same
 * raw object; checking pinType is the reliable discriminator between DI and DO.
 */
export const getRejectedFeatureKeys = (pin) => {
  const { selection, features, outputConflicts, canFeatures, pinType } = pin

  if (pinType === DIGITAL_OUTPUT) {
    if (isEmptyOrNil(outputConflicts)) return []
    return Object.entries(outputConflicts)
      .filter(([key]) => key !== selection)
      .flatMap(([, opt]) =>
        Object.values(opt.featureSet).flat().flatMap(getFeatureKeys)
      )
  }

  if (!isEmptyOrNil(canFeatures)) {
    return canFeatures
      .filter((f) => f.featureId !== selection)
      .flatMap(getFeatureKeys)
  }

  // Digital Input / Analog Input — features map, keys are configuration names
  if (isEmptyOrNil(features)) return []
  return Object.entries(features)
    .filter(([key]) => key !== selection)
    .flatMap(([, val]) =>
      (Array.isArray(val) ? val : [val]).flatMap(getFeatureKeys)
    )
}

/**
 * Remove rejected features from every unresolved conflict pin across all
 * conflict types. If pruning leaves only one option for a pin, auto-resolve it.
 *
 * This prevents the user from being offered — and selecting — a feature that
 * was already rejected by an earlier conflict resolution choice, which would
 * produce contradictory selections and stage zero features.
 *
 * Cascading: when a pin is auto-resolved by pruning, the features in its
 * losing options are themselves rejected and propagated to all remaining
 * unresolved pins. This repeats until no new auto-resolutions occur
 * (fixed-point iteration, capped at the initial number of unresolved pins —
 * a bound that guarantees convergence for valid conflict data because each
 * continuing pass must auto-resolve at least one pin).
 *
 * The optional originalConflicts parameter should be the pristine conflict
 * state received from the validation API, before any user confirmations have
 * pruned options from it. When supplied, it is used as the source for
 * reconstructing a pin's full option set when that pin auto-resolves — ensuring
 * bystander features from options pruned in *earlier user confirmations* are
 * still propagated. If omitted, conflicts itself is used (sufficient for
 * single-call scenarios where no prior pruning has occurred).
 *
 * Note: only options from *auto-resolved* pins are propagated. Options that
 * are pruned but leave a pin with multiple remaining choices do not cascade —
 * their bystander features are not yet definitively rejected because another
 * user selection could still pick them via a different path.
 *
 * Uses pin.pinType (not field-emptiness) to select the correct branch.
 */
export const pruneRejectedFeaturesFromConflicts = (
  conflicts,
  rejectedKeys,
  originalConflicts = null
) => {
  if (!rejectedKeys.size) return conflicts

  // Run a single prune pass over all conflict pins using the supplied rejected
  // key set. Returns the updated conflicts object.
  const singlePass = (current, currentRejected) => {
    const isRejected = (feature) =>
      getFeatureKeys(feature).some((k) => currentRejected.has(k))

    // outerType is the key from the conflict hierarchy (e.g. "digitalOutput").
    // It is used as a fallback when pin.pinType is absent from the API response.
    const prunePin = (pin, outerType) => {
      if (pin.selection !== null) return pin

      const effectivePinType = pin.pinType || outerType

      if (effectivePinType === DIGITAL_OUTPUT) {
        if (isEmptyOrNil(pin.outputConflicts)) return pin
        const pruned = { ...pin.outputConflicts }
        Object.entries(pruned).forEach(([key, opt]) => {
          if (Object.values(opt.featureSet).flat().some(isRejected))
            delete pruned[key]
        })
        const remaining = Object.keys(pruned)
        return {
          ...pin,
          outputConflicts: pruned,
          selection: remaining.length === 1 ? remaining[0] : null,
        }
      }

      if (!isEmptyOrNil(pin.canFeatures)) {
        const pruned = pin.canFeatures.filter((f) => !isRejected(f))
        return {
          ...pin,
          canFeatures: pruned,
          selection: pruned.length === 1 ? pruned[0].featureId : null,
        }
      }

      // Digital Input / Analog Input
      if (isEmptyOrNil(pin.features)) return pin
      const pruned = { ...pin.features }
      Object.entries(pruned).forEach(([key, val]) => {
        const features = Array.isArray(val) ? val : [val]
        if (features.some(isRejected)) delete pruned[key]
      })
      const remaining = Object.keys(pruned)
      return {
        ...pin,
        features: pruned,
        selection: remaining.length === 1 ? remaining[0] : null,
      }
    }

    const result = {}
    Object.entries(current).forEach(([type, typeGroup]) => {
      if (!typeGroup) {
        result[type] = typeGroup
        return
      }
      result[type] = {}
      Object.entries(typeGroup).forEach(([moduleName, moduleGroup]) => {
        result[type][moduleName] = {}
        Object.entries(moduleGroup).forEach(([pinKey, pin]) => {
          result[type][moduleName][pinKey] = prunePin(pin, type)
        })
      })
    })
    return result
  }

  // Collect all feature keys rejected by the losing options of an auto-resolved
  // pin (pins that just transitioned from selection===null to selection!==null).
  //
  // We use the ORIGINAL (pre-loop) pin state — not the current pass's "before"
  // state — to reconstruct the resolved pin for getRejectedFeatureKeys. This
  // ensures that options pruned in earlier passes are still present when we
  // extract losing feature keys, preventing a gap where bystander features from
  // a previously-pruned option are never propagated when the pin later resolves.
  //
  // When originalConflicts is supplied by the caller (representing the pristine
  // API state before any user confirmations have pruned the object), it takes
  // precedence over conflicts (which may already have options removed from
  // earlier confirmations). This extends the same guarantee across multiple
  // sequential pruneRejectedFeaturesFromConflicts calls.
  //
  // Example: pin has options A, B, C. Pass 1 prunes B (partial — no resolve).
  // Pass 2 prunes C → auto-selects A. Using the pass-1 "before" state (A + C)
  // would miss B's bystanders. Using the original state (A + B + C) catches them.
  const pinSource = originalConflicts ?? conflicts
  const collectNewlyRejectedKeys = (before, after) => {
    const newKeys = []
    Object.entries(after).forEach(([type, typeGroup]) => {
      if (!typeGroup) return
      Object.entries(typeGroup).forEach(([moduleName, moduleGroup]) => {
        Object.entries(moduleGroup).forEach(([pinKey, pin]) => {
          const prevPin = before[type]?.[moduleName]?.[pinKey]
          if (prevPin && prevPin.selection === null && pin.selection !== null) {
            // Reconstruct from the ORIGINAL pin (before any pass or prior call
            // removed options) so that all losing options contribute their keys.
            const originalPin =
              pinSource[type]?.[moduleName]?.[pinKey] ?? prevPin
            const pinWithSelection = {
              ...originalPin,
              selection: pin.selection,
            }
            newKeys.push(...getRejectedFeatureKeys(pinWithSelection))
          }
        })
      })
    })
    return newKeys
  }

  // Fixed-point loop: propagate rejections until no new auto-resolutions occur.
  //
  // The cap is derived from the number of initially-unresolved pins: since each
  // pass that produces new rejections must auto-resolve at least one pin, the
  // process is guaranteed to converge within N passes for valid conflict data.
  // A final cleanup pass is always applied after the loop to ensure any keys
  // collected on the last iteration are reflected in the returned state —
  // eliminating the "last-pass keys unapplied" gap.
  const countUnresolvedPins = (c) =>
    Object.values(c).reduce((typeAcc, typeGroup) => {
      if (!typeGroup) return typeAcc
      return (
        typeAcc +
        Object.values(typeGroup).reduce(
          (modAcc, moduleGroup) =>
            modAcc +
            Object.values(moduleGroup).filter((p) => p.selection === null)
              .length,
          0
        )
      )
    }, 0)

  const maxIterations = Math.max(1, countUnresolvedPins(conflicts))
  const accumulated = new Set(rejectedKeys)
  let current = conflicts
  let hitCap = false

  for (let i = 0; i < maxIterations; i += 1) {
    const next = singlePass(current, accumulated)
    const newlyRejected = collectNewlyRejectedKeys(current, next)

    current = next

    if (newlyRejected.length === 0) break // fixed point reached

    newlyRejected.forEach((k) => accumulated.add(k))

    if (i === maxIterations - 1) {
      hitCap = true
    }
  }

  // Apply any rejections collected on the final iteration that the loop had no
  // subsequent pass to use. For valid conflict data this pass is a no-op (all
  // remaining pins were already resolved); it only makes a difference for
  // malformed data where the loop converged on its cap iteration but
  // still produced new rejections.
  if (hitCap) {
    current = singlePass(current, accumulated)
    if (
      collectNewlyRejectedKeys(conflicts, current).length > 0 &&
      countUnresolvedPins(current) > 0
    ) {
      // eslint-disable-next-line no-console
      console.warn(
        '[pruneRejectedFeaturesFromConflicts] Iteration cap reached (%d) with ' +
          'unresolved pins still present. Conflict propagation may be incomplete. ' +
          'Check for malformed conflict data with circular or self-referential pin dependencies.',
        maxIterations
      )
    }
  }

  return current
}

/**
 * Auto-resolve any unresolved conflict pin that already has exactly one
 * option. This handles pins that arrive from the API with a single choice —
 * they should never require a user decision.
 *
 * Uses the same pinType / outerType discriminator as pruneRejectedFeaturesFromConflicts.
 */
export const autoResolveSingleOptionConflicts = (conflicts) => {
  const resolvePin = (pin, outerType) => {
    if (pin.selection !== null) return pin

    const effectivePinType = pin.pinType || outerType

    if (effectivePinType === DIGITAL_OUTPUT) {
      if (isEmptyOrNil(pin.outputConflicts)) return pin
      const remaining = Object.keys(pin.outputConflicts)
      return remaining.length === 1 ? { ...pin, selection: remaining[0] } : pin
    }

    if (!isEmptyOrNil(pin.canFeatures)) {
      return pin.canFeatures.length === 1
        ? { ...pin, selection: pin.canFeatures[0].featureId }
        : pin
    }

    if (isEmptyOrNil(pin.features)) return pin
    const remaining = Object.keys(pin.features)
    return remaining.length === 1 ? { ...pin, selection: remaining[0] } : pin
  }

  const result = {}
  Object.entries(conflicts).forEach(([type, typeGroup]) => {
    if (!typeGroup) {
      result[type] = typeGroup
      return
    }
    result[type] = {}
    Object.entries(typeGroup).forEach(([moduleName, moduleGroup]) => {
      result[type][moduleName] = {}
      Object.entries(moduleGroup).forEach(([pinKey, pin]) => {
        result[type][moduleName][pinKey] = resolvePin(pin, type)
      })
    })
  })
  return result
}

/**
 * Gets the set of switch panelNumbers currently on the vehicle.
 * Used to enrich newly-added features' ioSwitches with isSwitchOnVehicle.
 */
export const getVehicleSwitchPanelNumbers = (existingFeatures) => {
  const panelNumbers = new Set()
  if (!existingFeatures) return panelNumbers
  existingFeatures.forEach((feature) => {
    if (feature.isMarkedForDeletion) return
    const ioSwitches = feature.ioSwitches || []
    ioSwitches.forEach(({ switches = [] }) => {
      switches.forEach((sw) => {
        if (sw.isSwitchOnVehicle && sw.panelNumber) {
          panelNumbers.add(sw.panelNumber)
        }
      })
    })
  })
  return panelNumbers
}

/**
 * Enriches ioSwitches of newly-added features with isSwitchOnVehicle flag
 * by cross-referencing against the vehicle's existing switch panelNumbers.
 *
 * Only enriches an ioSwitch when the API hasn't already provided
 * isSwitchOnVehicle data (i.e., no switches are marked true).
 * This prevents inflation of on-vehicle counts when the API already
 * correctly identifies which switch is on the vehicle.
 */
export const enrichIoSwitchesWithVehicleData = (
  features,
  vehiclePanelNumbers
) => {
  if (!vehiclePanelNumbers || vehiclePanelNumbers.size === 0) return features
  return features.map((feature) => {
    const { ioSwitches } = feature
    if (!ioSwitches) return feature
    return {
      ...feature,
      ioSwitches: ioSwitches.map((ioSwitch) => {
        const switches = ioSwitch.switches || []
        // If any switch already has isSwitchOnVehicle from the API,
        // trust that data and skip enrichment to avoid inflating the count
        const hasApiData = switches.some((sw) => sw.isSwitchOnVehicle)
        if (hasApiData) return ioSwitch

        return {
          ...ioSwitch,
          switches: switches.map((sw) => ({
            ...sw,
            isSwitchOnVehicle: vehiclePanelNumbers.has(sw.panelNumber),
          })),
        }
      }),
    }
  })
}

/**
 * Returns a Set of signal IDs that are already present on the vehicle
 * (from existing features that are not marked for deletion).
 */
export const getSignalIdsOnVehicle = (existingFeatures) => {
  const signalIds = new Set()
  if (!existingFeatures) return signalIds
  existingFeatures.forEach((feature) => {
    if (feature.isMarkedForDeletion || feature.isMarkedForAddition) return
    const ioSwitches = feature.ioSwitches || []
    ioSwitches.forEach((ioSwitch) => {
      if (ioSwitch.id) signalIds.add(ioSwitch.id)
    })
  })
  return signalIds
}

/**
 * Every signal id in a feature set, staged additions included.
 *
 * Distinct from `getSignalIdsOnVehicle`, which deliberately skips features marked
 * for addition or deletion because it answers "what does the vehicle already
 * have". This answers "which signals does this set still cover", which is what
 * you need to decide whether a recorded switch assignment is still relevant.
 */
export const getAllSignalIds = (features) => {
  const signalIds = new Set()
  if (!features) return signalIds

  features.forEach((feature) => {
    const ioSwitches = (feature && feature.ioSwitches) || []
    ioSwitches.forEach((ioSwitch) => {
      if (ioSwitch.id) signalIds.add(ioSwitch.id)
    })
  })

  return signalIds
}

/**
 * For signals already on the vehicle, returns a map of { signalId: panelNumber }
 * picking the first on-vehicle switch. The commit flow filters out switches
 * already on the vehicle, so the exact choice doesn't matter — it just needs
 * a valid assignment to skip the selection modal.
 */
export const getPreSelectedSwitchesForExistingSignals = (
  newFeatures,
  existingSignalIds
) => {
  if (!existingSignalIds || existingSignalIds.size === 0) return {}
  const result = {}
  newFeatures.forEach((feature) => {
    const ioSwitches = feature.ioSwitches || []
    ioSwitches.forEach((ioSwitch) => {
      const { id, switches = [] } = ioSwitch
      if (!id || !existingSignalIds.has(id)) return
      if (switches.length <= 1) return
      // Signal already on vehicle — pick first on-vehicle switch
      const onVehicle = switches.find((sw) => sw.isSwitchOnVehicle)
      if (onVehicle) {
        result[id] = onVehicle.panelNumber
      }
    })
  })
  return result
}

/**
 * Feature metadata for every feature referenced by a set of mapping rows.
 *
 * The mapping rows can span several adds — a feature whose mapping modal was
 * dismissed earlier stays outstanding — while `selectedSearchFeatures` only ever
 * holds the latest selection. Both modals key off `row.featureId`, and neither
 * tolerates a row it has no feature for:
 *
 *   - FeatureInputSwitchSelectionModal destructures the match
 *     (`const { moduleName, partNumber } = features.find(...)`), so a missing
 *     entry throws and the user cannot complete the mapping at all;
 *   - FeatureInputModal iterates the feature list and filters rows by it, so a
 *     missing entry silently omits that feature's signals from the list.
 *
 * Sources are consulted in order and the first hit wins, so pass the staged set
 * first — its copies are authoritative — and `selectedSearchFeatures` as a
 * fallback. Rows with no match anywhere are dropped rather than yielding
 * undefined entries.
 *
 * @param rows outstanding mapping rows, each carrying `featureId`
 * @param featureSources feature lists to resolve against, most authoritative first
 */
export const getFeaturesForMappingRows = (rows, ...featureSources) => {
  const byId = new Map()

  featureSources.forEach((list) =>
    (list || []).forEach((feature) => {
      if (feature && feature.id && !byId.has(feature.id)) {
        byId.set(feature.id, feature)
      }
    })
  )

  const seen = new Set()

  return (rows || []).reduce((acc, row) => {
    const featureId = prop('featureId', row)

    if (!featureId || seen.has(featureId) || !byId.has(featureId)) return acc

    seen.add(featureId)
    return [...acc, byId.get(featureId)]
  }, [])
}

export const getIoSwitchesForMapping = (list, existingSignalIds) =>
  list.reduce((acc, curr) => {
    const { id } = curr
    const ioSwitches = curr.ioSwitches || []
    const ioSwitchConflicts = ioSwitches.reduce((accum, current) => {
      const { switches } = current
      if (length(switches) > 1) {
        // Skip signals already present on the vehicle — no selection needed
        if (existingSignalIds && existingSignalIds.has(current.id)) return accum
        const onVehicleCount = switches.filter(
          (sw) => sw.isSwitchOnVehicle
        ).length
        if (onVehicleCount === 1) return accum
        return [
          ...accum,
          {
            ...current,
            featureId: id,
          },
        ]
      }
      return accum
    }, [])

    if (ioSwitchConflicts.length) {
      return [...acc, ...ioSwitchConflicts]
    }
    return acc
  }, [])

/**
 * Every signal on the staged feature set that still needs a switch assigned.
 *
 * Derived from the staged set on each call rather than accumulated in state.
 * The previous approach kept one `ioSwitches` list plus an `allSwitchesMapped`
 * boolean, both overwritten by whichever add happened last, which meant the
 * answer drifted from reality in three separate ways:
 *
 *   - add A needing mapping, cancel, add B needing mapping, map B → completing
 *     B's modal asserted `allSwitchesMapped: true` for the whole session, so
 *     A's unassigned signals stopped blocking commit;
 *   - remove the feature that needed mapping → the list and the flag stayed
 *     behind and blocked commit for a mapping that no longer existed;
 *   - reopen mapping from a staged row → that row's list replaced the shared
 *     one, dropping any other feature's pending work.
 *
 * Recomputing removes all three: the only inputs are what is staged and what
 * has been assigned.
 *
 * Safe to call on the current `proposedSet` even though the add flow computed
 * its inputs from the pre-add set: `getSignalIdsOnVehicle` skips features marked
 * for addition or deletion, and `getVehicleSwitchPanelNumbers` only collects
 * switches already flagged `isSwitchOnVehicle`, so neither is polluted by the
 * staged additions themselves.
 *
 * @param proposedSet the current staged feature set
 * @param preSelectedSwitches signalId -> panelNumber, auto-assigned
 * @param addedSwitches signalId -> panelNumber, assigned by the user
 * @returns the ioSwitch entries still awaiting an assignment
 */
export const getOutstandingSwitchMappings = (
  proposedSet,
  preSelectedSwitches = {},
  addedSwitches = {}
) => {
  if (!proposedSet) return []

  const stagedAdditions = proposedSet.filter(
    (f) => f.isMarkedForAddition && !f.isMarkedForDeletion
  )

  if (!stagedAdditions.length) return []

  const enriched = enrichIoSwitchesWithVehicleData(
    stagedAdditions,
    getVehicleSwitchPanelNumbers(proposedSet)
  )

  return getIoSwitchesForMapping(
    enriched,
    getSignalIdsOnVehicle(proposedSet)
  ).filter(({ id }) => !preSelectedSwitches[id] && !addedSwitches[id])
}

export const getSingleOptionIoSwitches = (features) => {
  const singleOptionIoSwitches = features
    .map(({ ioSwitches = [], id }) => {
      if (!ioSwitches || !id) return null
      const ioSs = ioSwitches.filter(({ switches }) => {
        if (switches.length === 1) return true
        if (switches.length > 1) {
          const onVehicle = switches.filter((sw) => sw.isSwitchOnVehicle)
          return onVehicle.length === 1
        }
        return false
      })

      return ioSs.map((ioSwitch) => ({
        ...ioSwitch,
        featureId: id,
      }))
    })
    .flat()
    // remove nulls
    .reduce((acc, curr) => {
      if (!curr) return acc
      return [...acc, curr]
    }, [])

  if (!singleOptionIoSwitches.length) return {}

  const data = singleOptionIoSwitches.reduce((acc, curr) => {
    const id = pathOr(null, ['id'], curr)
    const { switches } = curr
    let panelNumber

    if (switches.length === 1) {
      panelNumber = pathOr(null, ['panelNumber'], switches[0])
    } else {
      const onVehicle = switches.find((sw) => sw.isSwitchOnVehicle)
      panelNumber = onVehicle ? onVehicle.panelNumber : null
    }

    if (id && panelNumber) {
      return {
        ...acc,
        [id]: panelNumber,
      }
    }

    return acc
  }, {})

  return data
}

const pwmModeValue = (pwmMode) => `${pwmMode} - ${pwmModeTitle(pwmMode)}`
const dutyCycleValue = (dutyCycle) =>
  `${dutyCycle}${strings.duty_cycle_label_append_symbol}`
const frequencyValue = (frequency) =>
  `${frequency}${strings.frequency_label_append_symbol}`

export const getConfigOutputValues = ({
  current,
  polarity,
  frequency,
  frequencyMin,
  frequencyMax,
  virtualFuseSetting,
  resetType,
  resetAttempts,
  dutyCycle,
  dutyCycleMin,
  dutyCycleMax,
  isAwake,
  powerNetLevel,
  resetDelay,
  pwmMode,
  connector,
  module,
  location,
  description,
}) => {
  const pwmItems = [
    {
      label: strings.mode,
      value: pwmModeValue(pwmMode),
    },
    ...insertIf(pwmMode !== PWM_MODES_MAPPING.VARIABLE_DUTY_CYCLE, {
      label: strings.duty_cycle,
      value: dutyCycleValue(dutyCycle),
    }),
    ...insertIf(
      pwmMode === PWM_MODES_MAPPING.VARIABLE_DUTY_CYCLE,
      {
        label: strings.duty_cycle_min,
        value: dutyCycleValue(dutyCycleMin),
      },
      {
        label: strings.duty_cycle_max,
        value: dutyCycleValue(dutyCycleMax),
      }
    ),
    ...insertIf(pwmMode !== PWM_MODES_MAPPING.VARIABLE_FREQUENCY, {
      label: strings.frequency,
      value: frequencyValue(frequency),
    }),
    ...insertIf(
      pwmMode === PWM_MODES_MAPPING.VARIABLE_FREQUENCY,
      {
        label: strings.frequency_min,
        value: frequencyValue(frequencyMin),
      },
      {
        label: strings.frequency_max,
        value: frequencyValue(frequencyMax),
      }
    ),
  ]

  const outputValues = [
    {
      label: strings.output_type_PIN,
      value: `${description}: ${module} ${connector}-${location}`,
    },
    {
      label: strings.current,
      value: `${current}${strings.amps_a}`,
    },
    {
      label: strings.polarity,
      value: polarityLabels[polarity],
    },
    ...insertIf(polarity === PWM, ...pwmItems),
    ...insertIf(
      current === AMPS20,
      {
        label: strings.virtual_fuse_setting,
        value: virtualFuseMask(virtualFuseSetting),
      },
      {
        label: strings.reset_virtual_fuse,
        value: virtualFuseReset(resetType, resetAttempts, resetDelay),
      }
    ),
    {
      label: strings.keep_awake,
      value: keepAwakeLabel(isAwake),
    },
    {
      label: strings.power_net_level,
      value: `${powerNetLevel} - ${powerNetLevels(powerNetLevel)}`,
    },
  ]

  return outputValues
}

export const makeKeepOrRemoveSwitchTableData = (
  removedSwitches,
  onGoingSet
) => {
  const findAssociatedFeaturesMeta = (match) => {
    const matchedFeatures = onGoingSet.filter(
      ({ isMarkedForDeletion, ioSwitches = [] }) => {
        if (!ioSwitches.length || !isMarkedForDeletion) return false

        const foundMatchedFeature = ioSwitches.find((ioSwitch) => {
          const { switches } = ioSwitch
          return switches.find(({ panelNumber }) => panelNumber === match)
        })

        return foundMatchedFeature
      }
    )

    const associatedFeatureMeta = matchedFeatures.map(
      ({ uri, partNumber }) => ({
        uri,
        partNumber,
      })
    )

    return associatedFeatureMeta
  }

  const data = removedSwitches.reduce((acc, { switches }) => {
    const switchData = switches.reduce(
      (
        acc,
        {
          isSwitchOnVehicle,
          panelNumber,
          type,
          panelDescription,
          usedOnAKeptFeature,
        }
      ) => {
        if (!isSwitchOnVehicle || usedOnAKeptFeature) return acc

        const associatedFeatures = findAssociatedFeaturesMeta(panelNumber)

        return [
          ...acc,
          {
            associatedFeatures,
            panelNumber,
            description: panelDescription,
            type,
          },
        ]
      },
      []
    )

    return [
      ...acc,
      ...switchData.map((data) => ({
        ...data,
        isMarkedForDeletion: false,
        isMarkedForAddition: true,
      })),
    ]
  }, [])

  const unigueData = Object.values(
    data.reduce(
      (acc, curr) => ({
        ...acc,
        [curr.panelNumber]: curr,
      }),
      {}
    )
  )

  return unigueData
}

const getSwitchPanelNumbersInKeptFeatures = (features) =>
  features
    .filter((f) => !f.isMarkedForDeletion && !f.isMarkedForAddition)
    .reduce((acc, curr) => [...acc, curr.ioSwitches], [])
    .flat()
    .filter((s) => Boolean(s))
    .map((s) => s.switches)
    .flat()
    .map((s) => s.panelNumber)

export const identifySwitchesUsedInKeptFeatures = (
  ios,
  features,
  addedSwitches
) => {
  const switchesUsedInKeptFeatures =
    getSwitchPanelNumbersInKeptFeatures(features)
  const addingFeaturesChoosenSwitches = Object.values(addedSwitches)
  const switchesUsedInKeptAddAddingFeatures = [
    ...switchesUsedInKeptFeatures,
    ...addingFeaturesChoosenSwitches,
  ]

  const updatedIOs = ios.map((ioSwitch) => {
    const { switches = [] } = ioSwitch
    const sws = switches.map((sw) => {
      // mark switches used in kept feature
      // if kept by another feature, user shouldn't be able to remove switch in keep/remove modal
      if (switchesUsedInKeptAddAddingFeatures.includes(sw.panelNumber)) {
        return {
          ...sw,
          usedOnAKeptFeature: true,
        }
      }
      return sw
    })

    return {
      ...ioSwitch,
      switches: sws,
    }
  })

  return updatedIOs
}

export const getKeepOrRemoveSwitchTableData = (onGoingSet, addedSwitches) => {
  const removedFeatures = onGoingSet.filter(prop('isMarkedForDeletion'))
  const ioSwitchesList = getIOSwitchesList(removedFeatures)
  const removedIOsWithSwitchesList = identifySwitchesUsedInKeptFeatures(
    ioSwitchesList,
    onGoingSet,
    addedSwitches
  )
  const keepRemoveSwitchTableData = makeKeepOrRemoveSwitchTableData(
    removedIOsWithSwitchesList,
    onGoingSet
  )
  return [keepRemoveSwitchTableData, removedIOsWithSwitchesList]
}

export const getUpdatedKeepOrRemoveSwitches = (
  keepOrRemoveSwitches,
  selectedKeepOrRemoveSwitches
) => {
  const updatedList = keepOrRemoveSwitches.map((sw) => {
    const { switches = [] } = sw
    if (switches.length) {
      const nextSwitches = switches.map((nextSwitch) => {
        const found = selectedKeepOrRemoveSwitches.find(
          (s) => s.panelNumber === nextSwitch.panelNumber
        )
        if (found) {
          return {
            ...nextSwitch,
            ...found,
          }
        }
        return nextSwitch
      })
      return {
        ...sw,
        switches: nextSwitches,
      }
    }
    return sw
  })

  return updatedList
}
